import type { RunComparison } from '../../types/benchmark';

interface ComparisonViewProps {
  comparison: RunComparison;
}

export function ComparisonView({ comparison }: ComparisonViewProps) {
  const getDeltaIcon = (delta: number) => {
    if (delta > 0.02) return '✅';
    if (delta < -0.02) return '⚠️';
    return '➡️';
  };

  const formatDelta = (delta: number, isPercentage: boolean = false) => {
    const sign = delta > 0 ? '+' : '';
    if (isPercentage) {
      return `${sign}${(delta * 100).toFixed(1)}%`;
    }
    return `${sign}${delta.toFixed(3)}`;
  };

  return (
    <div className="bg-white border rounded-lg p-6">
      <h2 className="text-2xl font-bold mb-6">
        Comparaison: {comparison.run_a.prompt_version} vs {comparison.run_b.prompt_version}
      </h2>

      {/* Main metrics table */}
      <div className="overflow-x-auto mb-6">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Métrique</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">{comparison.run_a.prompt_version}</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">{comparison.run_b.prompt_version}</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Delta</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            <tr>
              <td className="px-4 py-3 text-sm font-medium">F1</td>
              <td className="px-4 py-3 text-center text-sm">{Number(comparison.run_a.avg_f1).toFixed(3)}</td>
              <td className="px-4 py-3 text-center text-sm">{Number(comparison.run_b.avg_f1).toFixed(3)}</td>
              <td className="px-4 py-3 text-center text-sm font-semibold">
                {formatDelta(Number(comparison.delta.avg_f1))} {getDeltaIcon(Number(comparison.delta.avg_f1))}
              </td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-sm font-medium">Précision</td>
              <td className="px-4 py-3 text-center text-sm">{(Number(comparison.run_a.avg_precision) * 100).toFixed(1)}%</td>
              <td className="px-4 py-3 text-center text-sm">{(Number(comparison.run_b.avg_precision) * 100).toFixed(1)}%</td>
              <td className="px-4 py-3 text-center text-sm font-semibold">
                {formatDelta(Number(comparison.delta.avg_precision), true)} {getDeltaIcon(Number(comparison.delta.avg_precision))}
              </td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-sm font-medium">Tokens</td>
              <td className="px-4 py-3 text-center text-sm">{(Number(comparison.run_a.total_tokens) / 1000).toFixed(1)}k</td>
              <td className="px-4 py-3 text-center text-sm">{(Number(comparison.run_b.total_tokens) / 1000).toFixed(1)}k</td>
              <td className="px-4 py-3 text-center text-sm font-semibold">
                {formatDelta(comparison.delta.tokens_change_pct / 100, true)} {getDeltaIcon(-(comparison.delta.tokens_change_pct / 100))}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Errors by severity and improvements */}
      <div className="grid grid-cols-2 gap-6 mb-6">
        <div>
          <h3 className="text-lg font-semibold mb-3">Erreurs par sévérité</h3>
          <div className="space-y-2">
            <div className="flex justify-between items-center p-2 bg-red-50 rounded">
              <span className="text-sm">🔴 Critiques:</span>
              <span className="text-sm font-semibold">
                {comparison.severity_comparison.critical.run_a} → {comparison.severity_comparison.critical.run_b}
                {' '}({comparison.severity_comparison.critical.delta > 0 ? '+' : ''}{comparison.severity_comparison.critical.delta})
              </span>
            </div>
            <div className="flex justify-between items-center p-2 bg-orange-50 rounded">
              <span className="text-sm">🟡 Moyennes:</span>
              <span className="text-sm font-semibold">
                {comparison.severity_comparison.medium.run_a} → {comparison.severity_comparison.medium.run_b}
                {' '}({comparison.severity_comparison.medium.delta > 0 ? '+' : ''}{comparison.severity_comparison.medium.delta})
              </span>
            </div>
            <div className="flex justify-between items-center p-2 bg-yellow-50 rounded">
              <span className="text-sm">🟢 Mineures:</span>
              <span className="text-sm font-semibold">
                {comparison.severity_comparison.minor.run_a} → {comparison.severity_comparison.minor.run_b}
                {' '}({comparison.severity_comparison.minor.delta > 0 ? '+' : ''}{comparison.severity_comparison.minor.delta})
              </span>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-3">Améliorations</h3>
          {comparison.delta.improved_fields.length > 0 ? (
            <div className="space-y-2">
              {comparison.delta.improved_fields.slice(0, 5).map((field) => {
                const fieldData = comparison.by_field.find(f => f.field === field);
                const improvement = fieldData ? ((fieldData.f1_b - fieldData.f1_a) * 100).toFixed(1) : '0';
                return (
                  <div key={field} className="flex justify-between items-center p-2 bg-green-50 rounded">
                    <span className="text-sm truncate">{field}</span>
                    <span className="text-sm font-semibold text-green-700">+{improvement}% ✅</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-gray-500">Aucune amélioration significative</p>
          )}
        </div>
      </div>

      {/* Verdict */}
      <div className="bg-blue-50 border border-blue-200 rounded p-4">
        <div className="flex items-center gap-2">
          <span className="text-xl">📊</span>
          <span className="font-semibold">Verdict:</span>
          <span>
            {comparison.delta.avg_f1 > 0.01
              ? `${comparison.run_b.prompt_version} recommandé (↑ F1, ${comparison.severity_comparison.critical.delta < 0 ? '↓ erreurs critiques' : ''})`
              : comparison.delta.avg_f1 < -0.01
                ? `${comparison.run_a.prompt_version} recommandé`
                : 'Performance similaire'}
          </span>
        </div>
      </div>
    </div>
  );
}
